import { Link } from "react-router";
import { FrownIcon } from "lucide-react";
import { useCmsPageByCode } from "@/features/shop/cms/hooks/useCmsPageByCode";
import { extractZoneByCode } from "@/features/shop/cms/utils/cmsZoneExtractor";
import { NoiseCard, NoiseCardParagraph, NoiseCardTitle } from "@/features/shop/components/NoiseCard/NoiseCard";

const GRID_COLUMNS = 6;

const resolveContentSpan = (contentType?: string | null, fallback?: string): number | null => {
  const source = contentType ?? fallback;
  if (!source) {
    return null;
  }

  const match = source.toLowerCase().match(/(\d+)[/-](\d+)/);
  if (!match) {
    return null;
  }

  const span = Number(match[1]);
  const total = Number(match[2]);

  if (!Number.isFinite(span) || total !== GRID_COLUMNS) {
    return null;
  }

  return Math.min(Math.max(span, 1), GRID_COLUMNS);
};

const IMAGE_BASE_CLASSES = "absolute opacity-50 md:opacity-100 object-contain -z-10 transition-[transform,opacity] duration-300";
const IMAGE_SPAN_CLASSES: Record<number | "default", string> = {
  2: "bottom-0 right-0 object-bottom w-[130px] h-[130px] sm:w-[180px] sm:h-[180px] lg:w-[300px] lg:h-[300px]",
  4: "bottom-0 right-2 object-bottom w-[220px] h-[160px] sm:w-auto sm:h-[200px] lg:h-[220px]",
  default: "right-6 top-1/2 -translate-y-1/2 left-auto bottom-auto w-[280px] h-[280px] sm:w-[400px] sm:h-[240px] lg:w-[450px] lg:h-[260px]",
};
const SMALL_CARD_HEIGHT = "min-h-[200px] sm:min-h-[280px] md:min-h-[340px] lg:min-h-[400px]";

const PromotionCardErrorState = ({
  containerClassName,
  contentClassName,
}: {
  containerClassName: string
  contentClassName: string
}) => (
  <NoiseCard
    containerClassName={containerClassName}
    containerStyle={{ backgroundColor: "#b91c1c" }}
    className={contentClassName}
  >
    <div className="flex items-center justify-center h-full">
      <div className="flex flex-col items-center justify-center gap-4 text-center">
        <FrownIcon size={64} className="text-white" />
        <span className="text-xl font-medium text-white">Erreur lors de la récupération des promotions</span>
      </div>
    </div>
  </NoiseCard>
);

type PromotionCardContentProps = {
  block: {
    title: string
    subtitle?: string
    background_color: string
    cta_text?: string
    link_url: string
    media_url: string | null
    content_type?: string | null
  }
  zoneCode: string
  containerClassName: string
  contentClassName: string
};

const PromotionCardContent = ({
  block,
  zoneCode,
  containerClassName,
  contentClassName,
}: PromotionCardContentProps) => {
  const resolvedSpan = resolveContentSpan(block.content_type, zoneCode) ?? GRID_COLUMNS;
  const shouldRenderImage = !!block.media_url;
  const isSmallCard = resolvedSpan === 2;
  const isFullWidthCard = resolvedSpan === GRID_COLUMNS;
  const imagePositionClass = IMAGE_SPAN_CLASSES[resolvedSpan] ?? IMAGE_SPAN_CLASSES.default;
  const smallCardContentClassName = isSmallCard ? "py-8 sm:py-10" : "";
  const smallCardLayoutClassName = isSmallCard ? "justify-start" : "justify-start lg:justify-center xl:justify-start";
  const augmentedContainerClassName = isSmallCard
    ? `${containerClassName} ${SMALL_CARD_HEIGHT}`
    : containerClassName;

  return (
    <NoiseCard
      containerClassName={augmentedContainerClassName}
      containerStyle={{ backgroundColor: block.background_color || "#6b7280" }}
      className={`${contentClassName} ${smallCardContentClassName}`.trim()}
    >
      <div className={`relative h-full flex flex-col ${smallCardLayoutClassName}`}>
        <div className="max-w-lg">
          <NoiseCardTitle className={`max-w-sm md:max-w-lg leading-11 ${isSmallCard ? "text-2xl md:text-3xl" : ""}`}>
            {block.title}
          </NoiseCardTitle>
          {block.subtitle && (
            <NoiseCardParagraph className={isSmallCard ? "text-base md:text-lg" : ""}>
              {block.subtitle}
            </NoiseCardParagraph>
          )}
          {block.cta_text && block.link_url && (
            <div className="mt-4 md:mt-6">
              <Link
                to={block.link_url}
                className="inline-flex w-fit items-center justify-center rounded-full bg-white/100 px-2.5 py-1 text-xs font-semibold text-dark transition-all duration-200 hover:bg-primary hover:text-white hover:scale-105 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-transparent active:scale-95 sm:px-3 sm:py-1.5 sm:text-xs md:px-4 md:py-2 md:text-sm"
              >
                {block.cta_text}
              </Link>
            </div>
          )}
        </div>

        {shouldRenderImage && isFullWidthCard && (
          <img
            src={block.media_url ?? ""}
            alt={block.title}
            className={`${imagePositionClass} ${IMAGE_BASE_CLASSES}`}
          />
        )}
      </div>

      {shouldRenderImage && !isFullWidthCard && (
        <img
          src={block.media_url ?? ""}
          alt={block.title}
          className={`${imagePositionClass} ${IMAGE_BASE_CLASSES}`}
        />
      )}
    </NoiseCard>
  );
};

export type CmsPromotionCardProps = {
  pageCode: string
  zoneCode: string
  blockIndex?: number
  containerClassName?: string
  contentClassName?: string
};

export const CmsPromotionCard = ({
  pageCode,
  zoneCode,
  blockIndex = 0,
  containerClassName = "",
  contentClassName = "",
}: CmsPromotionCardProps) => {
  const { data: cmsPageDetail, isLoading, isError } = useCmsPageByCode({
    code: pageCode,
    enabled: !!pageCode,
  });

  if (isLoading || isError || !cmsPageDetail) {
    return null;
  }

  const zone = extractZoneByCode(cmsPageDetail.zones, zoneCode);

  if (!zone || zone.blocks.length === 0) {
    return (
      <PromotionCardErrorState
        containerClassName={containerClassName}
        contentClassName={contentClassName}
      />
    );
  }

  const sortedBlocks = [...zone.blocks].sort((a, b) => a.position - b.position);
  const block = sortedBlocks[blockIndex];

  if (!block) {
    return (
      <PromotionCardErrorState
        containerClassName={containerClassName}
        contentClassName={contentClassName}
      />
    );
  }

  return (
    <PromotionCardContent
      block={block}
      zoneCode={zone.code}
      containerClassName={containerClassName}
      contentClassName={contentClassName}
    />
  );
};
